import React from 'react';

// The data required to display a toast.
export type ToastPayload = {
  label: React.ReactNode;
  description?: React.ReactNode;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  autoClose?: boolean;
  displayIcon?: boolean;
  displayConfirm?: boolean;
  displayCancel?: boolean;
};

// Represents the possible visual types of the toast.
export type ToastType = 'success' | 'error' | 'warning' | 'info';

// The state is now a single, persistent object.
// The `type` can be null when no toast is active.
export type ToastState = ToastPayload & {
  show: boolean;
  type: ToastType | null;
};

// The actions that can be dispatched.
export type ToastAction = { type: 'DISPLAY_SUCCESS'; payload: ToastPayload } | { type: 'DISPLAY_ERROR'; payload: ToastPayload } | { type: 'DISPLAY_WARNING'; payload: ToastPayload } | { type: 'DISPLAY_INFO'; payload: ToastPayload } | { type: 'REMOVE_TOAST' };

// The default state for when the toast is hidden.
export const initialState: ToastState = {
  show: false,
  label: '',
  type: null,
};

/**
 * Manages the state of a single toast notification.
 * @param state - The current toast state object.
 * @param action - The action to perform.
 * @returns The new toast state object.
 */
const toastReducer = (state: ToastState = initialState, action: ToastAction): ToastState => {
  switch (action.type) {
    case 'DISPLAY_SUCCESS':
      return { ...action.payload, type: 'success', show: true };
    case 'DISPLAY_ERROR':
      return { ...action.payload, type: 'error', show: true };
    case 'DISPLAY_WARNING':
      return { ...action.payload, type: 'warning', show: true };
    case 'DISPLAY_INFO':
      return { ...action.payload, type: 'info', show: true };
    case 'REMOVE_TOAST':
      // Return the initial state to hide and reset the toast.
      return initialState;
    default:
      return state;
  }
};

export default toastReducer;
